import { mockBondsman1WAKE, mockBondsmanSearchResults } from './bondsman.mock';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable} from 'rxjs/Observable';
import { catchError, map, retryWhen, finalize } from 'rxjs/operators';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import { WrappedResponse } from '../shared/wrapped-response';
import { Bondsman } from './bondsman'
import { BondsmanIdentifier } from './../shared/bondsman-identifier';
import { CountyService } from '../shared/county.service';
import { environment } from './../../environments/environment';
import { BondsmanSearchResponse } from './bondsman-search-response';
import { FileNumberUtil } from '../shared/file-number-util';
import { CivilSearchConstants } from '../civil/civil-search-constants';
import { BondsmanSearchResultsResponse } from './bondsman-search-results-response';

@Injectable()
export class BondsmanSearchService {

  constructor(private http: HttpClient) { }

  /**
   * the bondsman cunty details by the state license id and the county
   */
  findByStateLicenseIdAndCounty (bondsmanIdentifier: BondsmanIdentifier, countyName: string): Observable<Bondsman> {
    // check if mock data is used
    let returnBondsman: Bondsman;
    for (let bondsmanResults of mockBondsmanSearchResults.data.bondsmanList) {
      let bondsman = new Bondsman(bondsmanResults);
       if (bondsman.getBondsmanIdentifier().equals(bondsmanIdentifier)) {
          returnBondsman = bondsman;
          break;
       }
    }

    if (returnBondsman) {
       return  Observable.of<Bondsman>(returnBondsman);
    }

    // --------else call web service to get real bondsman --------
    let url = environment.proxyBaseUrl
            + 'bondsmen/' + bondsmanIdentifier.stateLicenseId  + '/' + countyName;
    const startTime = Date.now();
    return this.http.get<WrappedResponse>(url).pipe(
      map(response => new Bondsman(response.data)),
      catchError(this.handleError),
      finalize(() => {
        // logging response time
        if (!environment.production) {
          const elapsed = Date.now() - startTime;
          console.log('Response Elapsed Time = ' + elapsed );
        }
     })
    );
  }

  /**
   * return json data for the forfeitures using the license number
   * @param licenseNumber: string
   */
  findForfeituresByLicenseNumber(licenseNumber: string): Observable<any> {
    // -------- call web service to get real bondsman --------
    let url = environment.proxyBaseUrl
            + 'bondsmen/forfeitures/' + licenseNumber;
    const startTime = Date.now();
    return this.http.get<WrappedResponse>(url).pipe(
      map(response => response.data),
      catchError(this.handleError),
      finalize(() => {
        // logging response time
        if (!environment.production) {
          const elapsed = Date.now() - startTime;
          console.log('Response Elapsed Time = ' + elapsed );
        }
     })
    );
  }

  /**
   * find the forfeitures by the given uri 
   * @param forfeituresUri string
   */
  findForfeituresByUri(forfeituresUri: string): Observable<any> {
    let url = environment.proxyBaseUrl + forfeituresUri;
    const startTime = Date.now();
    return this.http.get<WrappedResponse>(url).pipe(
      map(response => response.data),
      catchError(this.handleError),
      finalize(() => {
        // logging response time
        if (!environment.production) {
          const elapsed = Date.now() - startTime;
          console.log('Response Elapsed Time = ' + elapsed );
        }
     })
    );
  }

  /**
   * search bondsman by lincese number
   * @param licenseNumber
   */
  searchBondsmanByLicenseNumber(licenseNumber: string): Observable<BondsmanSearchResponse> {
    // return the mock bondsman if mock license number is used for the search
    let returnBondsman: Bondsman;
    for (let bondsmanResults of mockBondsmanSearchResults.data.bondsmanList) {
      let bondsman = new Bondsman(bondsmanResults);
       if (bondsman.getBondsmanIdentifier().licenseNumber === licenseNumber) {
          returnBondsman = bondsman;
          break;
       }
    }
    if (returnBondsman) {
      const result: BondsmanSearchResponse = { bondsmanList: [], pageNumber: 1, pageSize: 25, totalRecords: 1 };
      result.bondsmanList.push(returnBondsman);
      return Observable.of(result);
    }

    // ------------- call the web search to do search ------------
    let url = environment.proxyBaseUrl
            + 'bondsmen/search?doi_license_number=' + licenseNumber;
    const startTime = Date.now();
    return this.http.get<BondsmanSearchResultsResponse>(url).pipe(
      map(searchResultsResponse => this.buildBondsmanSearchResponse(searchResultsResponse)),
      catchError(this.handleError),
      finalize(() => {
        // logging response time
        if (!environment.production) {
          const elapsed = Date.now() - startTime;
          console.log('Response Elapsed Time = ' + elapsed );
        }
     })
    );
  }

  /**
   * search bondsman list by bondsman names
   * @param bondsmanSearchForm
   */
  searchBondsmanByName(bondsmanSearchForm: any): Observable<BondsmanSearchResponse> {
    let endpointName;
    let params = new HttpParams();
    const searchForm = bondsmanSearchForm.suretyNameSearchGroup; // name form
    const matchType = this.translateMatchType(searchForm.searchNameMatchType);

    if (searchForm.searchBusinessNames ) {
      endpointName = 'bondsmen/business/search';
      params = params.set('name', FileNumberUtil.normalizeRegularString(searchForm.businessName));
      params = params.append('match_type', matchType);
    } else {
      endpointName = 'bondsmen/person/search';
      if (FileNumberUtil.normalizeRegularString(searchForm.lastName)
        || FileNumberUtil.normalizeRegularString(searchForm.firstName)
        || FileNumberUtil.normalizeRegularString(searchForm.middleName) ) {

        if (searchForm.lastName) {
          params = params.append('last_name', FileNumberUtil.normalizeRegularString(searchForm.lastName));
          params = params.append('ln_match_type', matchType);
        }

        if (searchForm.firstName) {
          params = params.append('first_name', FileNumberUtil.normalizeRegularString(searchForm.firstName));
          params = params.append('fn_match_type', matchType);
        }

        if (searchForm.middleName) {
          params = params.append('middle_name', FileNumberUtil.normalizeRegularString(searchForm.middleName));
          params = params.append('mn_match_type', matchType);
        }
      }
      /*
      else {
        endpointName = 'search'; // endpoint for person first name, last name and middle name empty
      } */
    }

    const pageNumber = bondsmanSearchForm.pageNumber || 1;
    params = params.append('page', pageNumber);
    params = params.append('size', CivilSearchConstants.PAGE_SIZE.toString());

    // form the url
    const url = `${environment.proxyBaseUrl}${endpointName}`;
    if (!environment.production) {
      console.log('URL=' + url + '?' + params);
    }

    return this.http.get<BondsmanSearchResultsResponse>(url, {params})
        .pipe(
            retryWhen(function(errors) { // retry 5xx errors once after a 250ms delay
              return errors.scan(function(errorCount, err) {
                  const shouldRetry = err instanceof HttpErrorResponse && err.status >= 500;
                  if (!shouldRetry || errorCount >= 1) {
                      throw err;
                  }
                  return errorCount + 1;
              }, 0).delay(250);
            }),
            map(searchResultsResponse => this.buildBondsmanSearchResponse(searchResultsResponse)),
            catchError(this.handleError)
        );
  }

  private handleError(error: HttpErrorResponse) {
    // TODO: if this error handling is consistent across all http calls,
    // we can push this code to CiprsHttp and avoid repeating the logic
    let errorMessage: string;
    console.log(error);
    if (error.status === 0 ||
        error.status >= 500) { // TODO: this line is here because of nginx issues which no longer exist. Remove it?
      console.error('error code = ' + error.status, error);
      errorMessage = 'Unable to reach the server. Please try again later.';
    } else {
      const wrappedErrorResponse = error.error || {};
      if (wrappedErrorResponse.responseCode === 404) {
        errorMessage = 'No matching records were found.';
      } else {
        errorMessage = 'The server encountered an error'; // TODO: what should the user be directed to do?
      }
    }
    console.error('errorMessage=' + errorMessage);
    return new ErrorObservable(errorMessage);
  }

  private buildBondsmanSearchResponse(searchResultsResponse: BondsmanSearchResultsResponse): BondsmanSearchResponse {
    const result: BondsmanSearchResponse = {
      bondsmanList: [],
      pageNumber: searchResultsResponse.data.pageNumber,
      pageSize: searchResultsResponse.data.pageSize,
      totalRecords: searchResultsResponse.data.totalRecords
    };

    for (let jsonResult of searchResultsResponse.data.bondsmanList) {
      result.bondsmanList.push(new Bondsman(jsonResult));
    }
    return result;
  }

  private translateMatchType(matchTypeUiValue: string): string {
    if (!matchTypeUiValue)  {
       return 'EXACT';
    }
    return matchTypeUiValue.toUpperCase() === 'Exact Match'.toUpperCase() ? 'EXACT' : 'STARTSWITH';
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/bondsman/bondsman-search.service.ts